import { getErrorMessage } from '@fruitful/fruitils';
import type { DepositAccount } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    ControlledInput,
    ControlledPicker,
    Form,
    FullScreenSpinner,
    isWeb,
    SafeAreaView,
    SmoothieScrollView,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import SelectAccountSheet from '../../../banking/components/transfers/SelectAccountSheet';
import useGetAllBankAccountDetails from '../../../banking/hooks/useGetAllBankAccountDetails';
import useGetInternalAndExternalDepositAccounts from '../../../banking/hooks/useGetInternalAndExternalDepositAccounts';
import ControlledDepositAccountSelect from '../../../common/components/controlled/ControlledDepositAccountSelect';
import { assertNotNullish } from '../../../common/misc';
import { captureException, captureMessage } from '../../../logger/Logger';
import { useGetApexAccountDetails } from '../../hooks/useGetApexAccountDetails';
import useTestICTWithdrawal, {
    RetirementDistributionCreateType,
    type TestICTWithdrawalRequest,
} from '../../hooks/useTestICTWithdrawal';

const depositAccountSchema = z.object({
    id: z.string(),
    isExternal: z.boolean(),
    accountNumberMasked: z.string(),
    accountType: z.string(),
    bankName: z.string(),
    nickname: z.string(),
    availableBalance: z.number().optional(),
    imgBase64: z.string().optional(),
    logo: z.string().optional(),
    icon: z.string().optional(),
    color: z.string().optional(),
    accountNumberMaskedIsTokenized: z.boolean(),
    nonTokenizedAccountNumberMasked: z.string().optional(),
});

const distributionTypeOptions = Object.values(RetirementDistributionCreateType)
    .filter(
        (type) => type !== RetirementDistributionCreateType.TYPE_UNSPECIFIED,
    )
    .map((type) => ({
        label: type.replace(/_/g, ' '),
        value: type,
    }));

const createIctWithdrawalSchema = (isRetirementAccount: boolean) =>
    z
        .object({
            fromApexAccountId: z.string().min(1, 'Apex Account ID is required'),
            toDepositAccount: depositAccountSchema.nullable(),
            amountCents: z.string().optional(),
            memo: z.string().optional(),
            fullDisbursement: z.boolean(),
            distributionType: z
                .nativeEnum(RetirementDistributionCreateType)
                .optional(),
        })
        .refine(({ toDepositAccount }) => toDepositAccount != null, {
            message: 'Please select an account',
            path: ['toDepositAccount'],
        })
        .refine(
            ({ fullDisbursement, amountCents }) =>
                fullDisbursement || (amountCents?.length ?? 0) > 0,
            {
                message: 'Please enter an amount or select full disbursement',
                path: ['amountCents'],
            },
        )
        .refine(
            ({ distributionType }) =>
                !isRetirementAccount || distributionType != null,
            {
                message:
                    'Distribution type is required for retirement accounts',
                path: ['distributionType'],
            },
        );

type ICTWithdrawalSchema = z.infer<
    ReturnType<typeof createIctWithdrawalSchema>
>;

const SimulateICTWithdrawal = () => {
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const toast = useToastController();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSelectToAccountSheetOpen, setIsSelectToAccountSheetOpen] =
        useState(false);
    const [lastResponse, setLastResponse] = useState<string | null>(null);

    const { mutateAsync: testICTWithdrawal } = useTestICTWithdrawal();
    const { refetchAccountDetails } = useGetAllBankAccountDetails(false);

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalAndExternalDepositAccounts({});

    const { data: apexAccountDetails, isLoading: isLoadingApexAccount } =
        useGetApexAccountDetails(apexAccountId ?? '');

    const isRetirementAccount =
        apexAccountDetails?.isRetirementAccount ?? false;

    // Filter to internal accounts only
    const internalAccounts = (depositAccounts ?? []).filter(
        (account) => !account.isExternal,
    );

    const {
        register,
        control,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm<ICTWithdrawalSchema>({
        resolver: zodResolver(createIctWithdrawalSchema(isRetirementAccount)),
        defaultValues: {
            fromApexAccountId: apexAccountId ?? '',
            toDepositAccount: null,
            amountCents: '',
            memo: '',
            fullDisbursement: false,
            distributionType: RetirementDistributionCreateType.NORMAL,
        },
    });

    const fullDisbursement = useWatch({ control, name: 'fullDisbursement' });

    const onSubmit: SubmitHandler<ICTWithdrawalSchema> = async ({
        fromApexAccountId,
        toDepositAccount,
        amountCents,
        memo,
        fullDisbursement: isFullDisbursement,
        distributionType,
    }) => {
        if (toDepositAccount == null) {
            return;
        }

        setIsSubmitting(true);

        toast.show('Submitting ICT withdrawal...', {
            duration: 3000,
        });

        try {
            const request: TestICTWithdrawalRequest = {
                fromApexAccountId: assertNotNullish(fromApexAccountId),
                toSyncteraAccountId: assertNotNullish(toDepositAccount.id),
                memo,
                fullDisbursement: isFullDisbursement,
                ...(!isFullDisbursement &&
                    amountCents != null && {
                        amountCents: parseInt(amountCents, 10),
                    }),
                ...(isRetirementAccount &&
                    distributionType != null && {
                        retirementDistribution: {
                            type: distributionType,
                        },
                    }),
            };

            const response = await testICTWithdrawal({
                body: request,
            });

            setLastResponse(JSON.stringify(response, null, 2));

            const successMessage =
                response.stateMessage != null && response.stateMessage !== ''
                    ? `ICT withdrawal simulated: ${response.stateMessage}`
                    : 'ICT withdrawal simulated successfully';
            toast.show(successMessage, {
                duration: 10000,
            });
            captureMessage(
                `ICT withdrawal simulated: ${JSON.stringify(response)}`,
            );

            // Refresh account details
            void refetchAccountDetails();
        } catch (e) {
            captureException(e);
            setLastResponse(
                JSON.stringify({ error: getErrorMessage(e) }, null, 2),
            );
            toast.show(`Error! ${getErrorMessage(e)}`, {
                duration: 10000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (apexAccountId == null) {
        captureException(
            new Error(
                'Missing apexAccountId query param for ICT withdrawal simulation',
            ),
        );
        router.back();
        return null;
    }

    if (isLoadingDepositAccounts || isLoadingApexAccount) {
        return <FullScreenSpinner />;
    }

    const handleSelectToAccount = (account: DepositAccount) => {
        setValue('toDepositAccount', account, { shouldValidate: true });
    };

    const handleFullDisbursementChange = (checked: boolean) => {
        setValue('fullDisbursement', checked, { shouldValidate: true });
        if (checked) {
            setValue('amountCents', '', { shouldValidate: true });
        }
    };

    return (
        <SafeAreaView>
            <SmoothieScrollView>
                <View>
                    <BodyLargeEmphasized alignSelf="center">
                        Simulate ICT Withdrawal
                    </BodyLargeEmphasized>
                    <View marginTop={-26} marginBottom={24}>
                        <BackButton
                            fallbackRoute="/home/investment"
                            iconType={BackButtonIconType.ARROW_LEFT}
                        />
                    </View>
                </View>
                <YStack
                    maxWidth={700}
                    alignSelf="center"
                    marginTop={isWeb ? 24 : 0}
                    marginHorizontal={16}
                >
                    <View marginBottom={16}>
                        <Form onSubmit={handleSubmit(onSubmit)} flex={1}>
                            <YStack gap={12} flex={1} minWidth={500}>
                                <ControlledInput
                                    name="fromApexAccountId"
                                    label={`From (Apex Account)${apexAccountDetails?.nickname != null ? ` - ${apexAccountDetails.nickname}` : ''}${apexAccountDetails?.registrationType != null ? ` / ${apexAccountDetails.registrationType}` : ''}`}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    editable={false}
                                    disabled
                                />
                                <ControlledDepositAccountSelect
                                    name="toDepositAccount"
                                    label="To (Syncteria Account)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    placeholder="Select your account"
                                    onPress={() => {
                                        setIsSelectToAccountSheetOpen(true);
                                    }}
                                />

                                <XStack alignItems="center" gap={8}>
                                    <Checkbox
                                        checked={fullDisbursement}
                                        onCheckedChange={
                                            handleFullDisbursementChange
                                        }
                                        id="fullDisbursement"
                                    />
                                    <BodyLargeEmphasized>
                                        Full Disbursement (withdraw entire
                                        balance)
                                    </BodyLargeEmphasized>
                                </XStack>

                                <ControlledInput
                                    name="amountCents"
                                    label="Amount (cents)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    type="numeric"
                                    inputMode="numeric"
                                    disabled={fullDisbursement}
                                    placeholder={
                                        fullDisbursement
                                            ? 'Full balance will be withdrawn'
                                            : ''
                                    }
                                />
                                <ControlledInput
                                    name="memo"
                                    label="Memo"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                />

                                <ControlledPicker
                                    name="distributionType"
                                    label="Distribution Type (Retirement)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={distributionTypeOptions}
                                    disabled={!isRetirementAccount}
                                />

                                <Button
                                    tone="positive"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    text="Simulate ICT withdrawal"
                                    onPress={handleSubmit(onSubmit)}
                                    disabled={!isValid || isSubmitting}
                                />

                                {lastResponse != null && (
                                    <View
                                        backgroundColor="$gray100"
                                        padding={12}
                                        borderRadius={8}
                                        marginTop={16}
                                    >
                                        <BodyLargeEmphasized marginBottom={8}>
                                            Response:
                                        </BodyLargeEmphasized>
                                        <BodySmallRegular
                                            fontFamily="$monoBody"
                                            whiteSpace="pre-wrap"
                                        >
                                            {lastResponse}
                                        </BodySmallRegular>
                                    </View>
                                )}
                            </YStack>
                        </Form>
                    </View>

                    <SelectAccountSheet
                        isOpen={isSelectToAccountSheetOpen}
                        depositAccounts={internalAccounts}
                        onClose={() => {
                            setIsSelectToAccountSheetOpen(false);
                        }}
                        onPlaidExit={() => {
                            setIsSelectToAccountSheetOpen(false);
                        }}
                        onSelectAccount={handleSelectToAccount}
                        enableLinkingNewAccount={false}
                        onNeedsReview={() => {}}
                    />
                </YStack>
            </SmoothieScrollView>
        </SafeAreaView>
    );
};

export default SimulateICTWithdrawal;
